import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { profilePdfPath } from "../lib/profile";
import useReveal from "../hooks/useReveal";
import { Send, Download, Linkedin, Instagram, Youtube, Mail, PhoneCall, MapPin } from "lucide-react";
import XIcon from "../components/XIcon";
import LegendsMark from "../components/LegendsMark";
import { toast } from "sonner";

const LOCATION_MAP_URL = "https://maps.google.com/maps?svid=CAwSHRIbCgNwdnESFENnMHZaeTh4TVhBd2VubDBOM0Z1GAo&um=1&ie=UTF-8&fb=1&gl=sa&sa=X&ftid=0x3e2ee17fdd39e7db:0xe1fa78d8f15c6622";

export default function ContactPage() {
    const { t, isRTL, lang } = useI18n();
    const { settings } = useSiteSettings();
    const ref = useReveal();
    const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        api.get("/company-profile").then((r) => setProfile(r.data)).catch(() => {});
    }, []);

    const email = settings.contact_email || "info@Legends.sa";
    const phone = settings.contact_phone || "";
    const address = (lang === "ar" ? settings.contact_address_ar : settings.contact_address_en) ||
        (lang === "ar" ? "الرياض، السعودية" : "Riyadh, KSA");
    const socials = [
        { url: settings.social_linkedin, Icon: Linkedin, label: "LinkedIn" },
        { url: settings.social_instagram, Icon: Instagram, label: "Instagram" },
        { url: settings.social_x, Icon: XIcon, label: "X" },
        { url: settings.social_youtube, Icon: Youtube, label: "YouTube" },
    ].filter((s) => s.url);
    const profilePath = profilePdfPath(profile, lang);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", form);
            setDone(true);
            toast.success(t("contact_success"));
            setForm({ name: "", email: "", company: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div ref={ref} data-testid="contact-page">
            <section className="bg-[#002951] text-white py-24 md:py-36 relative overflow-hidden">
                <div className="grain-overlay" />
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 plus-mark">
                            {t("section_contact_overline")}
                        </div>
                        <h1 className="heading page-hero-oneline">
                            {t("section_contact_title")}
                        </h1>
                        <p className="mt-8 text-lg text-white/70 max-w-xl">
                            {lang === "ar"
                                ? "تواصل مع فريق ليجيندز لمناقشة مشروعك التالي. نُحوّل الأفكار إلى أثر."
                                : "Reach out to the Legends team to discuss your next project. We turn ideas into impact."}
                        </p>
                        <div className="mt-12 flex flex-wrap gap-4">
                            {profilePath && (
                                <a
                                    href={fileUrl(profilePath)}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#002951] px-6 py-4 text-sm uppercase tracking-[0.2em] transition-colors"
                                    data-testid="contact-download-profile"
                                >
                                    <Download size={16} />
                                    {(lang === "ar" ? profile.button_label_ar : profile.button_label_en) || t("download_profile")}
                                </a>
                            )}
                            <div className="flex items-center gap-4">
                                {socials.map(({ url, Icon, label }) => (
                                    <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                                       className="hover:text-[#2BB0D4] transition-colors" aria-label={label}>
                                        <Icon size={22} />
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
                            <div className="border border-white/12 bg-white/[0.04] p-5">
                                <div className="mb-4 flex h-10 w-10 items-center justify-center bg-[#0060A7] text-white">
                                    <Mail size={18} />
                                </div>
                                <div className="text-xs uppercase tracking-[0.3em] text-white/45 mb-2">Email</div>
                                <a href={`mailto:${email}`} className="heading text-xl md:text-2xl legends-link break-words" data-testid="contact-email-display">{email}</a>
                            </div>
                            {phone && (
                                <div className="border border-white/12 bg-white/[0.04] p-5">
                                    <div className="mb-4 flex h-10 w-10 items-center justify-center bg-[#0060A7] text-white">
                                        <PhoneCall size={18} />
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.3em] text-white/45 mb-2">{lang === "ar" ? "الهاتف" : "Phone"}</div>
                                    <a href={`tel:${phone}`} className="heading text-xl md:text-2xl legends-link tabular-nums" data-testid="contact-phone-display">{phone}</a>
                                </div>
                            )}
                            <div className="border border-white/12 bg-white/[0.04] p-5">
                                <div className="mb-4 flex h-10 w-10 items-center justify-center bg-[#0060A7] text-white">
                                    <MapPin size={18} />
                                </div>
                                <div className="text-xs uppercase tracking-[0.3em] text-white/45 mb-2">{lang === "ar" ? "المواقع" : "Locations"}</div>
                                <a
                                    href={LOCATION_MAP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="heading text-xl md:text-2xl legends-link"
                                    data-testid="contact-address-display"
                                >
                                    {address}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-5">
                        <form
                            onSubmit={onSubmit}
                            className="bg-white text-[#0A0A0A] p-8 md:p-10 relative reveal"
                            data-testid="contact-form"
                        >
                            <LegendsMark size={20} color="#0060A7" className="absolute top-4 right-4" />
                            <h2 className="heading text-2xl tracking-tight mb-6">
                                {lang === "ar" ? "أرسل لنا رسالة" : "Send us a message"}
                            </h2>
                            <div className="space-y-4">
                                {[
                                    { k: "name", l: t("contact_name"), req: true },
                                    { k: "email", l: t("contact_email"), req: true, type: "email" },
                                    { k: "company", l: t("contact_company") },
                                    { k: "subject", l: t("contact_subject") },
                                ].map((f) => (
                                    <div key={f.k}>
                                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{f.l}</label>
                                        <input
                                            type={f.type || "text"}
                                            required={f.req}
                                            value={form[f.k]}
                                            onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                                            data-testid={`contact-${f.k}`}
                                            className="w-full border-b border-black/15 focus:border-[#0060A7] outline-none py-2 mt-1 bg-transparent"
                                        />
                                    </div>
                                ))}
                                <div>
                                    <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{t("contact_message")}</label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        data-testid="contact-message"
                                        className="w-full border-b border-black/15 focus:border-[#0060A7] outline-none py-2 mt-1 bg-transparent resize-none"
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="contact-submit"
                                className="mt-8 w-full bg-[#002951] hover:bg-[#0060A7] disabled:opacity-50 text-white px-6 py-4 text-sm uppercase tracking-[0.25em] transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <Send size={16} />
                                {busy ? "…" : t("contact_send")}
                            </button>
                            {done && <p className="mt-4 text-sm text-emerald-600" data-testid="contact-success">{t("contact_success")}</p>}
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
